// @flow

import type {InjectIntlProvidedProps} from 'react-intl';
import type {FieldProps} from 'redux-form';
import type {RootState} from '../../../reducers/types';
import type {
  DictionaryItem,
  DisputeResultAccepted,
  DisputeResultLost,
  DisputeResultNew,
  DisputeResultNoActionsRequired,
  DisputeResultWon,
  PaymentMethod,
  SearchFilters
} from '../types';

import React, {PureComponent} from 'react';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {Field, FieldArray, formValueSelector, reduxForm} from 'redux-form';
import {MultiSelect} from '@xsolla/design-system-controls';
import {DesignSystemProvider} from '@xsolla/design-system-core';
import {publisher} from '@xsolla/design-system-themes';
import {
  Button,
  DatePickerInputRange,
  FormGroup,
  Input,
  Select,
  SelectMultiple
} from '@xsolla/uikit';
import classNames from 'classnames/bind';
import moment from 'moment';
import {compose} from 'recompose';
import {setAvailable as setAvailableReport} from '../../../reducers/disputes/actions/report';
import {getDisputes, resetDisputes, setFilter} from '../../../reducers/disputes/actions/table';
import {
  selectFeatureProtectUserIdField,
  selectPaymentMethods,
  selectProjects,
  selectTableFilter
} from '../../../reducers/disputes/selectors';
import {formatDate} from '../../../utils/datetime';
import {validate} from '../../../validators/support/disputes';
import style from './style.css';

// Wrapper component to make MultiSelect compatible with redux-form FieldArray
type MultiSelectFieldProps = {
  +fields: {
    removeAll: () => void,
    push: (val: number) => void,
    getAll: () => Array<number> | null
  },
  +options: Array<{children: string, value: number}>,
  +placeholder?: string,
  +onChange?: (values: Array<number>) => void,
  +disabled?: boolean
};

const MultiSelectField = ({
  fields,
  options,
  placeholder,
  onChange,
  disabled
}: MultiSelectFieldProps) => {
  const handleChange = (newValues: Array<number>) => {
    // Clear existing values
    fields.removeAll();
    // Add new values
    newValues.forEach(val => fields.push(val));
    // Call custom onChange if provided
    if (onChange) {
      onChange(newValues);
    }
  };

  const currentValues = fields.getAll() || [];

  return (
    <DesignSystemProvider theme={publisher}>
      <MultiSelect
        placeholder={placeholder}
        contextMenuSettings={{
          type: 'checkbox'
        }}
        extraClear={true}
        onChange={handleChange}
        options={options}
        value={currentValues}
        disabled={disabled}
        flexible={false}
        size="sm"
      />
    </DesignSystemProvider>
  );
};

type Props = {
  +merchantID: number,
  +selectedInstances: Array<number>,
  +featureProtectUserIdField: boolean,
  +dictionary: {
    +projects: Array<DictionaryItem>,
    +paymentMethods: Array<PaymentMethod>
  },
  +disputes: {
    search: (merchantID: number, filters: SearchFilters) => Promise<void>,
    reset: () => void
  },
  +filter: {
    reset: () => void
  }
} & InjectIntlProvidedProps &
  FieldProps;

const cx = classNames.bind(style);
export const FORM_NAME = 'disputes-search';

class Search extends PureComponent<Props> {
  componentDidMount() {
    const {initialValues} = this.props;
    this.onSubmit(initialValues);
  }

  defaultFilters = () => {
    const {reset, filter} = this.props;

    filter.reset();
    reset();
  };

  defaultReset = () => {
    const {disputes} = this.props;

    disputes.reset();
  };

  onSubmit = (values: SearchFilters) => {
    const {disputes, merchantID} = this.props;
    this.defaultReset();

    disputes.search(merchantID, values);
  };

  onReset = () => {
    this.defaultReset();
    this.defaultFilters();
  };

  render() {
    const {
      intl,
      dictionary,
      selectedInstances,
      handleSubmit,
      dispatch,
      change,
      featureProtectUserIdField
    } = this.props;
    const {projects, paymentMethods} = dictionary;

    // Get all payment methods that have any of the selected instances
    const selectedPMs =
      selectedInstances && selectedInstances.length > 0
        ? paymentMethods.filter(pm =>
            pm.instances.some(instance => selectedInstances.includes(instance.id))
          )
        : [];

    // Merge reasons from all selected payment methods (deduplicated by id)
    const mergedReasons = selectedPMs.reduce((acc, pm) => {
      pm.reasons.forEach(reason => {
        if (!acc.some(r => r.id === reason.id)) {
          acc.push(reason);
        }
      });
      return acc;
    }, []);

    return (
      <form className={cx('disputes-search')} onSubmit={handleSubmit(this.onSubmit)}>
        <div className={cx('disputes-search__filter')}>
          {!featureProtectUserIdField && (
            <div className={cx('disputes-search__field', 'disputes-search__field--md')}>
              <FormGroup label={intl.messages['app.support.disputes.filter.user-id.label']}>
                <Field name="userId" component={Input} size="sm" offset="left" icon="search" />
              </FormGroup>
            </div>
          )}
          <div className={cx('disputes-search__field')}>
            <FormGroup
              label={intl.messages['app.support.disputes.filter.period.label']}
              tooltip={intl.messages['app.support.disputes.filter.period.tooltip']}>
              <Field
                name="period"
                component={DatePickerInputRange}
                disabledDays={{
                  after: moment()
                    .subtract(1, 'days')
                    .toDate()
                }}
                size="sm"
                position="top"
                align="right"
                placeholder={intl.messages['app.support.disputes.filter.period.placeholder']}
              />
            </FormGroup>
          </div>
          <div className={cx('disputes-search__field', 'disputes-search__field--sm')}>
            <FormGroup label={intl.messages['app.support.disputes.filter.project.label']}>
              <FieldArray
                name="projects"
                component={SelectMultiple}
                options={projects.map(project => ({label: project.name, value: project.id}))}
                size="sm"
                placeholder={intl.messages['app.support.disputes.filter.project.placeholder']}
                searchable={true}
              />
            </FormGroup>
          </div>
          <div className={cx('disputes-search__field', 'disputes-search__field--sm')}>
            <FormGroup label={intl.messages['app.support.disputes.filter.payment-method.label']}>
              <FieldArray
                name="instances"
                component={MultiSelectField}
                options={paymentMethods.flatMap(pm =>
                  pm.instances.map(instance => ({
                    children: instance.name || pm.paymentMethod.name,
                    value: instance.id
                  }))
                )}
                onChange={() => dispatch(change('reasons', []))}
                placeholder={
                  intl.messages['app.support.disputes.filter.payment-method.placeholder']
                }
              />
            </FormGroup>
          </div>
          <div className={cx('disputes-search__field', 'disputes-search__field--lg')}>
            <FormGroup
              label={intl.messages['app.support.disputes.filter.reason.label']}
              tooltip={intl.messages['app.support.disputes.filter.reason.tooltip']}>
              <FieldArray
                name="reasons"
                component={SelectMultiple}
                options={mergedReasons.map(reason => ({
                  label: reason.name,
                  value: reason.id
                }))}
                size="sm"
                placeholder={intl.messages['app.support.disputes.filter.reason.placeholder']}
                disabled={selectedPMs.length === 0}
              />
            </FormGroup>
          </div>
          <div className={cx('disputes-search__field', 'disputes-search__field--md')}>
            <FormGroup label={intl.messages['app.support.disputes.filter.result.label']}>
              <FieldArray
                name="results"
                component={SelectMultiple}
                options={[
                  {
                    label: intl.messages['app.support.disputes.result.new'],
                    value: (1: DisputeResultNew)
                  },
                  {
                    label: intl.messages['app.support.disputes.result.accepted'],
                    value: (2: DisputeResultAccepted)
                  },
                  {
                    label: intl.messages['app.support.disputes.result.in-progress'],
                    value: (6: DisputeResultNoActionsRequired)
                  },
                  {
                    label: intl.messages['app.support.disputes.result.won'],
                    value: (7: DisputeResultWon)
                  },
                  {
                    label: intl.messages['app.support.disputes.result.lost'],
                    value: (8: DisputeResultLost)
                  }
                ]}
                size="sm"
                placeholder={intl.messages['app.support.disputes.filter.result.placeholder']}
              />
            </FormGroup>
          </div>
          <div className={cx('disputes-search__field', 'disputes-search__field--sm')}>
            <FormGroup label={intl.messages['app.support.disputes.filter.action-required.label']}>
              <Field
                name="partnerEvidenceRequestStatus"
                component={Select}
                options={[
                  {
                    label: intl.messages['app.support.disputes.filter.action-required.all'],
                    value: null
                  },
                  {
                    label: intl.messages['app.support.disputes.filter.action-required.yes'],
                    value: 'awaiting_response'
                  },
                  {
                    label: intl.messages['app.support.disputes.filter.action-required.no'],
                    value: 'no'
                  }
                ]}
                size="sm"
                placeholder={
                  intl.messages['app.support.disputes.filter.action-required.placeholder']
                }
              />
            </FormGroup>
          </div>
        </div>
        <div className={cx('disputes-search__buttons')}>
          <div className={cx('disputes-search__button', 'disputes-search__button--lg')}>
            <Button type="submit" appearance="secondary" size="sm">
              {intl.messages['app.support.disputes.button.search']}
            </Button>
          </div>
          <div
            className={cx(
              'disputes-search__button',
              'disputes-search__button--sm',
              'disputes-search__button--last'
            )}>
            <Button appearance="flatten" size="sm" onClick={this.onReset}>
              {intl.messages['app.support.disputes.button.clear']}
            </Button>
          </div>
        </div>
      </form>
    );
  }
}

const selector = formValueSelector(FORM_NAME);

const mapStateToProps = (state: RootState) => ({
  projects: selectProjects(state),
  paymentMethods: selectPaymentMethods(state),
  selectedInstances: selector(state, 'instances') || [],
  initialValues: selectTableFilter(state),
  featureProtectUserIdField: selectFeatureProtectUserIdField(state)
});

const mapDispatchToProps = {
  getDisputes,
  resetDisputes,
  setFilter,
  setAvailableReport
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  selectedInstances: stateProps.selectedInstances,
  featureProtectUserIdField: stateProps.featureProtectUserIdField,
  dictionary: {
    projects: stateProps.projects,
    paymentMethods: stateProps.paymentMethods
  },
  disputes: {
    search: dispatchProps.getDisputes,
    reset: dispatchProps.resetDisputes
  },
  filter: {
    reset: () => {
      dispatchProps.setFilter();
      dispatchProps.setAvailableReport();
    }
  },
  initialValues: {
    instances: stateProps.initialValues.instance || [],
    projects: stateProps.initialValues.project,
    reasons: stateProps.initialValues.reason,
    results: stateProps.initialValues.status,
    period: {
      from: formatDate(stateProps.initialValues.inDateBegin),
      to: formatDate(stateProps.initialValues.inDateFinish)
    },
    userId: stateProps.initialValues.userId,
    partnerEvidenceRequestStatus: stateProps.initialValues.partnerEvidenceRequestStatus
  }
});

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    validate
  }),
  injectIntl
);

export default enhance(Search);
